import * as z from 'zod'

import { POSSIBLE_FIN_CATEGORY_KEYS } from '../finCategories'
import raw from './taxes.json'

// The discriminator for every tax calculator in `#calculator/Taxer`.
// Owned by the taxes feature — the enum's values are the primary keys in
// `taxes.json`.
export enum TaxKey {
  TaxFederalIncome = 'TaxFederalIncome',
  TaxStateIncome = 'TaxStateIncome',
  TaxMedicare = 'TaxMedicare', // TODO: #could rename to TaxMedicareW2
  TaxSocialSecurity = 'TaxSocialSecurity', // TODO: #could rename to TaxSocialSecurityW2
  TaxCapitalGains = 'TaxCapitalGains',
  TaxStateProperty = 'TaxStateProperty',
  TaxNetInvestmentIncome = 'TaxNetInvestmentIncome',
  TaxMedicareSE = 'TaxMedicareSE',
  TaxSocialSecuritySE = 'TaxSocialSecuritySE',
  TaxAdditionalMedicare = 'TaxAdditionalMedicare',
  TaxAlternativeMinimum = 'TaxAlternativeMinimum',
}

// TaxMetadata — the shape the calculator actually reads. Anything a
// calculator branches on lives here.
export const taxMetadataSchema = z.object({
  key: z.enum(TaxKey),
  name: z.string().min(1),
  fillingCategoryKey: z.enum(POSSIBLE_FIN_CATEGORY_KEYS),
  growthRate: z.number().min(0),
})

export type TaxMetadata = z.infer<typeof taxMetadataSchema>

// UnusedTaxMetadata — reference-only documentation fields, not consumed
// by any calculator code today. Kept validated so a bad edit to
// taxes.json still throws, and surfaced in the playground so humans
// can eyeball tax shape. Do NOT read these fields in production code —
// behavior is encoded in the calculator class hierarchy instead.
export const unusedTaxMetadataSchema = z.object({
  isCalculationPerPerson: z.boolean(),
  bracketType: z
    .enum([
      'Federal',
      'State',
      'Medicare',
      'SocialSecurity',
      'CapitalGains',
      'AMT',
    ])
    .nullable(),
  calculationType: z.enum(['Brackets', 'Simplified', 'Thresholds']).nullable(),
})

export type UnusedTaxMetadata = z.infer<typeof unusedTaxMetadataSchema>

// Full source row = used + unused; playground displays every column.
const taxSourceRowSchema = taxMetadataSchema.extend(
  unusedTaxMetadataSchema.shape
)

export type TaxSourceRow = z.infer<typeof taxSourceRowSchema>

export const TAXES: readonly TaxSourceRow[] = z
  .array(taxSourceRowSchema)
  .parse(raw)

// TAX_BY_KEY strips to used-only. Calculator code that reads a tax by key
// gets a shape matching what it should actually depend on — no hidden
// coupling to fields we've flagged as unused.
const stripUnused = ({
  key,
  name,
  fillingCategoryKey,
  growthRate,
}: TaxSourceRow): TaxMetadata => ({
  key,
  name,
  fillingCategoryKey,
  growthRate,
})

export const TAX_BY_KEY: ReadonlyMap<TaxKey, TaxMetadata> = new Map(
  TAXES.map(tax => [tax.key, stripUnused(tax)])
)
